import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {ClipboardCheck, Search, Plus, Edit, Trash2, CheckCircle, AlertTriangle, AlertCircle, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, Users, DollarSign, Package, MapPin, Calendar, FileText, Clock, TrendingUp, TrendingDown, AlertOctagon, Boxes, Tag} from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { InventaireModalPortal, InventaireViewModalPortal } from './components/inventaireModalPortal';
import FacturationFormModalTabs from '../facturation/components/FacturationFormModalTabs';
import {
  mapInventaireStatCards,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
  INVENTAIRE_TABLE_ACTIONS_TH_CLASS,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  nextInventaireEntityRef,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
  fetchInventaireWarehouses,
  buildInventaireWarehouseSelectOptions,
  buildInventaireProductSelectOptions,
  mapInventaireWarehouseSelection,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { fetchCatalogProducts } from '../articles/catalogProductOptions';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import '../../shared/components/custom/CustomComponents.css';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { useTranslation } from 'react-i18next';
import { formatBrandMoney, getBrandCurrencySuffix, getBrandLocale } from '../../shared/utils/brandCurrency';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
// Tables-mobile.css - DELETED (not needed)

const InventairePhysiquePage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.inventairePhysiquePage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const currencySuffix = getBrandCurrencySuffix();
  const { settings } = useSettings();
  const inventoryConfig = useInventoryConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);
  useArticlesConfig();
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString(dateLocale) : '—');
  const formatVarianceValue = (value) => {
    const n = parseFloat(value);
    if (Number.isNaN(n) || n === 0) return `0 ${currencySuffix}`;
    return formatBrandMoney(Math.abs(n));
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [inventories, setInventories] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [inventoryFormTab, setInventoryFormTab] = useState('inventoryInfo');

  const inventoryFormTabs = useMemo(() => ([
    { id: 'inventoryInfo', label: p('section_inventory_info'), icon: ClipboardCheck, iconColor: 'text-blue-400' },
    { id: 'varianceAnalysis', label: p('section_variance_analysis'), icon: AlertTriangle, iconColor: 'text-orange-400' },
  ]), [p]);

  
  const [formData, setFormData] = useState({ 
    inventory_date: '',
    warehouse_id: '',
    warehouse_name: '',
    counted_by: '',
    verified_by: '',
    status: 'planned',
    product_id: '',
    product_code: '',
    product_name: '',
    expected_quantity: '',
    counted_quantity: '',
    variance_quantity: '',
    variance_value: '',
    reason: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_inventories');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // GPS-style controls state
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  useEffect(() => { 
    loadInventories();
    loadProducts();
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      setWarehouses(await fetchInventaireWarehouses());
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setWarehouses([]);
    }
  };

  const loadInventories = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/physical-inventory');
      setInventories(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading inventories:', error);
      setInventories([]);
    }
  };

  const loadProducts = async () => {
    try {
      setProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const stats = {
    total: inventories.length,
    completed: inventories.filter(i => i.status === 'completed').length,
    in_progress: inventories.filter(i => i.status === 'in_progress').length,
    with_variance: inventories.filter(i => parseFloat(i.variance_quantity || 0) !== 0).length
  };

  const mainTabs = [
    { id: 'all_inventories', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: CheckCircle },
    { id: 'par_ecart', name: p('tab_by_variance'), icon: TrendingDown }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'planned', name: p('filter_planned'), icon: Calendar },
    { id: 'in_progress', name: p('filter_in_progress'), icon: Clock },
    { id: 'completed', name: p('filter_completed'), icon: CheckCircle },
    { id: 'with_variance', name: p('filter_with_variance'), icon: AlertTriangle }
  ];

  const inventoriesDistribution = {
    all_inventories: {
      title: p('overview_title'),
      data: [
        { id: 'planned', name: p('filter_planned'), count: inventories.filter(i => i.status === 'planned').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'in_progress', name: p('filter_in_progress'), count: stats.in_progress, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { id: 'completed', name: p('filter_completed'), count: stats.completed, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'with_variance', name: p('filter_with_variance'), count: stats.with_variance, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: [
        { id: 'planned', name: p('filter_planned'), count: inventories.filter(i => i.status === 'planned').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'in_progress', name: p('filter_in_progress'), count: stats.in_progress, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { id: 'completed', name: p('filter_completed'), count: stats.completed, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'cancelled', name: p('filter_cancelled'), count: inventories.filter(i => i.status === 'cancelled').length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' }
      ]
    },
    par_ecart: {
      title: p('distribution_by_variance'),
      data: [
        { id: 'no_variance', name: p('filter_no_variance'), count: inventories.filter(i => parseFloat(i.variance_quantity || 0) === 0).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'surplus', name: p('filter_surplus'), count: inventories.filter(i => parseFloat(i.variance_quantity || 0) > 0).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'shortage', name: p('filter_shortage'), count: inventories.filter(i => parseFloat(i.variance_quantity || 0) < 0).length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    }
  };

  const distributionInfo = inventoriesDistribution[mainViewMode] || inventoriesDistribution.all_inventories;

  const baseFilteredInventories = inventories.filter(i => {
    if (!inventaireRecordInDateRange(dateRange, i.inventory_date, i.created_at)) return false;

    if (searchTerm && !i.inventory_number?.toLowerCase().includes(searchTerm.toLowerCase()) && !i.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !i.warehouse_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_inventories') {
      if (quickFilter === 'planned' && i.status !== 'planned') return false;
      if (quickFilter === 'in_progress' && i.status !== 'in_progress') return false;
      if (quickFilter === 'completed' && i.status !== 'completed') return false;
      if (quickFilter === 'with_variance' && parseFloat(i.variance_quantity || 0) === 0) return false;
    }
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_inventories':
          if (activeDistributionFilter === 'planned' && i.status !== 'planned') return false;
          if (activeDistributionFilter === 'in_progress' && i.status !== 'in_progress') return false;
          if (activeDistributionFilter === 'completed' && i.status !== 'completed') return false;
          if (activeDistributionFilter === 'with_variance' && parseFloat(i.variance_quantity || 0) === 0) return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'planned' && i.status !== 'planned') return false;
          if (activeDistributionFilter === 'in_progress' && i.status !== 'in_progress') return false;
          if (activeDistributionFilter === 'completed' && i.status !== 'completed') return false;
          if (activeDistributionFilter === 'cancelled' && i.status !== 'cancelled') return false;
          break;
        case 'par_ecart':
          if (activeDistributionFilter === 'no_variance' && parseFloat(i.variance_quantity || 0) !== 0) return false;
          if (activeDistributionFilter === 'surplus' && parseFloat(i.variance_quantity || 0) <= 0) return false;
          if (activeDistributionFilter === 'shortage' && parseFloat(i.variance_quantity || 0) >= 0) return false;
          break;
      }
    }
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('inventory_number'),
    { key: 'inventory_date', getValue: (r) => formatDate(r.inventory_date) },
    textColumnRule('warehouse_name'),
    textColumnRule('product_name'),
    textColumnRule('variance_quantity', (r) => String(r.variance_quantity ?? '')),
    exactColumnRule('status', (r) => r.status),
  ], [formatDate]);

  const filteredInventories = useMemo(
    () => applyColumnFilters(baseFilteredInventories, colFilters, columnFilterRules),
    [baseFilteredInventories, colFilters, columnFilterRules],
  );
  const visibleInventories = useMemo(
    () => sliceInventaireListItems(filteredInventories, itemsPerPage),
    [filteredInventories, itemsPerPage],
  );

  const generateInventoryNumber = useCallback(
    () => nextInventaireEntityRef(settings, 'physicalInventory', 'PINV', inventories, (i) => i.inventory_number),
    [settings, inventories],
  );

  const openNewInventoryModal = () => {
    if (!inventoryConfig.enablePhysicalInventory) {
      window.alert(p('notice_physical_inventory_disabled', { defaultValue: 'الجرد الفعلي معطّل من إعدادات المخزون.' }));
      return;
    }
    setEditingRecord(null);
    setInventoryFormTab('inventoryInfo');
    setFormData({
      inventory_number: inventoryConfig.autoGeneratePhysicalInventoryReference !== false ? generateInventoryNumber() : '',
      inventory_date: new Date().toISOString().split('T')[0],
      warehouse_id: '',
      warehouse_name: '',
      counted_by: '',
      verified_by: '',
      status: 'planned',
      product_code: '',
      product_name: '',
      expected_quantity: '',
      counted_quantity: '',
      variance_quantity: '',
      variance_value: '',
      reason: '',
      notes: '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const inventoryNumber = (formData.inventory_number || '').trim() || generateInventoryNumber();
      const submitData = { ...formData, inventory_number: inventoryNumber };
      if (editingRecord) {
        await localApiAdapter.put(`/inventory/physical-inventory/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الجرد' }));
      } else {
        await localApiAdapter.post('/inventory/physical-inventory', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الجرد' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error'));
      return;
    } finally {
      resetForm();
      loadInventories();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setInventoryFormTab('inventoryInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/inventory/physical-inventory/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الجرد' }));
        loadInventories();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error'));
      }
    }
  };

  const filteredInventoriesIds = useMemo(() => filteredInventories.map((r) => r.id), [filteredInventories]);
  const allRowsSelected = filteredInventoriesIds.length > 0 && filteredInventoriesIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredInventoriesIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };
  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/inventory/physical-inventory/${id}`)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'الجرد' }));
        setSelectedRecords([]);
        loadInventories();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      inventory_number: '', inventory_date: '', warehouse_id: '', warehouse_name: '', counted_by: '', verified_by: '',
      status: 'planned', product_code: '', product_name: '', expected_quantity: '', counted_quantity: '',
      variance_quantity: '', variance_value: '', reason: '', notes: ''
    });
    setEditingRecord(null);
    setInventoryFormTab('inventoryInfo');
    setShowModal(false);
  };


  // Import/Export handlers
  const handleImport = () => {
    triggerFileInput((file) => {
      importFromFile(file, (data) => {
        console.log('Imported data:', data);
        // Process import data here
        loadInventories();
      });
    });
  };

  const handleExport = () => {
    exportToCSV(filteredInventories, 'inventaires_physiques');
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      await generateReportPDF(
        p('report_title'),
        filteredInventories,
        ['inventory_number', 'inventory_date', 'warehouse_name', 'product_name', 'status'],
        'inventaires_physiques'
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    setIsGeneratingExcel(true);
    try {
      await generateReportExcel(
        p('report_title'),
        filteredInventories,
        ['inventory_number', 'inventory_date', 'warehouse_name', 'product_name', 'status'],
        'inventaires_physiques'
      );
    } catch (error) {
      console.error('Error generating Excel:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const statusOptions = [
    { value: 'planned', label: p('status_planned'), icon: Calendar, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'in_progress', label: p('status_in_progress'), icon: Clock, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'completed', label: p('status_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
  ];


  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/inventory/physical-inventory/${recordId}`, { status: newStatus });
      await loadInventories();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getVarianceBadge = (variance) => {
    const val = parseFloat(variance || 0);
    if (val === 0) return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle size={14} />{p('variance_compliant')}</span>;
    if (val > 0) return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-blue-500/20 text-blue-400 border-blue-500/30"><TrendingUp size={14} />{p('variance_surplus', { value: val })}</span>;
    return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-red-500/20 text-red-400 border-red-500/30"><TrendingDown size={14} />{p('variance_shortage', { value: val })}</span>;
  };

  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'inventory_number', labelKey: 'col_inventory_number' },
    { key: 'inventory_date', labelKey: 'col_date' },
    { key: 'warehouse_name', labelKey: 'col_warehouse' },
    { key: 'product_name', labelKey: 'col_product' },
    { key: 'variance_quantity', labelKey: 'col_variance' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const inventaireStatCards = useMemo(() => mapInventaireStatCards([
    { key: 'total', label: p('stat_total'), value: stats.total, icon: ClipboardCheck },
    { key: 'completed', label: p('stat_completed'), value: stats.completed, icon: CheckCircle },
    { key: 'in_progress', label: p('stat_in_progress'), value: stats.in_progress, icon: Clock },
    { key: 'with_variance', label: p('stat_with_variance'), value: stats.with_variance, icon: AlertTriangle },
  ]), [stats, p]);


  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: p('generating'),
    isGeneratingPDF,
    handlers: {
      onImport: handleImport,
      onExportCSV: handleExport,
      onExportPDF: handleExportPDF,
      onExportExcel: handleExportExcel,
    },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, t, p]);

  return (
    <>
    <InventoryConfigNotice
      message={!inventoryConfig.enablePhysicalInventory ? p('notice_physical_inventory_disabled_banner', { defaultValue: 'الجرد الفعلي معطّل — فعّله من إعدادات المخزون ← الجرد الفعلي.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={ClipboardCheck}
      statEntries={inventaireStatCards}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      onCreate={openNewInventoryModal}
      createLabel={p('new_count')}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
      importExport={importExportProps}
    >
              {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  {isColVisible('inventory_number') && (

                    <TableColumnHeaderFilter columnKey="inventory_number" label={p('col_inventory_number')} value={colFilters.inventory_number ?? ''} onChange={(v) => setColFilter('inventory_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('inventory_date') && (

                    <TableColumnHeaderFilter columnKey="inventory_date" label={p('col_date')} value={colFilters.inventory_date ?? ''} onChange={(v) => setColFilter('inventory_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('warehouse_name') && (

                    <TableColumnHeaderFilter columnKey="warehouse_name" label={p('col_warehouse')} value={colFilters.warehouse_name ?? ''} onChange={(v) => setColFilter('warehouse_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('product_name') && (

                    <TableColumnHeaderFilter columnKey="product_name" label={p('col_product')} value={colFilters.product_name ?? ''} onChange={(v) => setColFilter('product_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('variance_quantity') && (

                    <TableColumnHeaderFilter columnKey="variance_quantity" label={p('col_variance')} value={colFilters.variance_quantity ?? ''} onChange={(v) => setColFilter('variance_quantity', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  {isColVisible('status') && (

                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={INVENTAIRE_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {visibleInventories.length > 0 ? (
                  visibleInventories.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {isColVisible('inventory_number') && (

                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.inventory_number}</span></td>

                      )}
                      {isColVisible('inventory_date') && (

                        <td className="px-6 py-4"><span className="text-white/70">{formatDate(record.inventory_date)}</span></td>

                      )}
                      {isColVisible('warehouse_name') && (

                        <td className="px-6 py-4"><span className="text-white/70">{record.warehouse_name || '-'}</span></td>

                      )}
                      {isColVisible('product_name') && (

                        <td className="px-6 py-4"><span className="text-white/90">{record.product_name}</span></td>

                      )}
                      {isColVisible('variance_quantity') && (

                        <td className="px-6 py-4 text-center"><div className="flex justify-center">{getVarianceBadge(record.variance_quantity)}</div></td>

                      )}
                      {isColVisible('status') && (

                        <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div></td>

                      )}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><ClipboardCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleInventories.length > 0 ? (
              visibleInventories.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><ClipboardCheck size={20} className="text-blue-400" />{record.inventory_number}</h3>
                        <div className="flex gap-2"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} />{getVarianceBadge(record.variance_quantity)}</div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_general_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_date')}:</span> {formatDate(record.inventory_date)}</div>
                            <div><span className="font-medium">{p('label_warehouse')}:</span> {record.warehouse_name || '-'}</div>
                            <div><span className="font-medium">{p('label_counted_by')}:</span> {record.counted_by || '-'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_count')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_product')}:</span> {record.product_name}</div>
                            <div><span className="font-medium">{p('label_expected')}:</span> {record.expected_quantity || 0}</div>
                            <div><span className="font-medium">{p('label_counted')}:</span> {record.counted_quantity || 0}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_variance_analysis')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_variance_qty')}:</span> {record.variance_quantity || 0}</div>
                            <div><span className="font-medium">{p('label_variance_value')}:</span> {formatVarianceValue(record.variance_value)}</div>
                            <div><span className="font-medium">{p('label_reason')}:</span> {record.reason || '-'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><ClipboardCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.2 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><ClipboardCheck size={18} className="text-blue-400" />{p('section_general_info')}</h3>
                  <div className={INVENTAIRE_FORM_GRID}>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_inventory_number')}</p><p className="text-white font-medium">{viewingRecord.inventory_number}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_date')}</p><p className="text-white font-medium">{formatDate(viewingRecord.inventory_date)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_status')}</p><div className="mt-1"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_warehouse')}</p><p className="text-white font-medium">{viewingRecord.warehouse_name || '-'}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_counted_by')}</p><p className="text-white font-medium">{viewingRecord.counted_by || '-'}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_verified_by')}</p><p className="text-white font-medium">{viewingRecord.verified_by || '-'}</p></div>
                  </div>
                </div>
                <div className={INVENTAIRE_FORM_GRID}>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_count_details')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('label_product_code')}</p><p className="text-white font-medium">{viewingRecord.product_code || '-'}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_product_name')}</p><p className="text-white font-medium">{viewingRecord.product_name}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_expected_qty')}</p><p className="text-white font-medium">{viewingRecord.expected_quantity || 0}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_counted_qty')}</p><p className="text-white font-medium">{viewingRecord.counted_quantity || 0}</p></div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_variance_analysis')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('label_variance')}</p><div className="mt-1">{getVarianceBadge(viewingRecord.variance_quantity)}</div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_variance_quantity')}</p><p className="text-white font-medium">{viewingRecord.variance_quantity || 0}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_variance_value')}</p><p className="text-white font-medium">{formatVarianceValue(viewingRecord.variance_value)}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_reason')}</p><p className="text-white font-medium">{viewingRecord.reason || '-'}</p></div>
                    </div>
                  </div>
                </div>
                {viewingRecord.notes && (
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_notes')}</h3>
                    <p className="text-white/70">{viewingRecord.notes}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {showModal && (
        <InventaireModalPortal show={showModal} onClose={resetForm} maxWidth="1200px">
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('edit_inventory') : p('new_count')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="inventory-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('update') : t('common.create')}</button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <FacturationFormModalTabs
              tabs={inventoryFormTabs}
              activeTab={inventoryFormTab}
              onTabChange={setInventoryFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('edit_inventory') : p('new_count')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="inventory-form" onSubmit={handleSubmit}>
              {inventoryFormTab === 'inventoryInfo' && (
              <div className={INVENTAIRE_FORM_GRID}>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_inventory_number', { defaultValue: 'رمز الجرد' })}</label>
                            <input
                              type="text"
                              required
                              value={formData.inventory_number || ''}
                              onChange={(e) => setFormData({ ...formData, inventory_number: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                              placeholder={p('placeholder_inventory_number', { defaultValue: 'INV-001' })}
                              dir="ltr"
                            />
                          </div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label><input type="date" required value={formData.inventory_date} onChange={(e) => setFormData({ ...formData, inventory_date: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'planned', label: p('status_planned') },
                                { value: 'in_progress', label: p('status_in_progress') },
                                { value: 'completed', label: p('status_completed') },
                                { value: 'cancelled', label: p('status_cancelled') }
                              ]}
                              placeholder={p('placeholder_select_status')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_warehouse')}</label>
                            <CustomSelect
                              value={formData.warehouse_id}
                              onChange={(value) => setFormData({
                                ...formData,
                                ...mapInventaireWarehouseSelection(value, warehouses),
                              })}
                              options={buildInventaireWarehouseSelectOptions(warehouses)}
                              placeholder={p('placeholder_select_warehouse')}
                              searchable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_counted_by')}</label>
                            <CustomSelect
                              value={formData.counted_by}
                              onChange={(value) => setFormData({ ...formData, counted_by: value })}
                              options={[
                                { value: p('role_stock_manager'), label: p('role_stock_manager') },
                                { value: p('role_warehouse_manager'), label: p('role_warehouse_manager') },
                                { value: p('role_supervisor'), label: p('role_supervisor') }
                              ]}
                              placeholder={p('placeholder_counter_name')}
                              searchable={true}
                              creatable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_verified_by')}</label>
                            <CustomSelect
                              value={formData.verified_by}
                              onChange={(value) => setFormData({ ...formData, verified_by: value })}
                              options={[
                                { value: p('role_stock_manager'), label: p('role_stock_manager') },
                                { value: p('role_warehouse_manager'), label: p('role_warehouse_manager') },
                                { value: p('role_supervisor'), label: p('role_supervisor') },
                                { value: p('role_director'), label: p('role_director') }
                              ]}
                              placeholder={p('placeholder_verifier_name')}
                              searchable={true}
                              creatable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_product_name')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.product_id}
                              onChange={(value) => {
                                const selectedProduct = products.find((prod) => String(prod.id) === String(value));
                                if (selectedProduct) {
                                  setFormData({
                                    ...formData,
                                    product_id: value,
                                    product_name: selectedProduct.name || '',
                                    product_code: selectedProduct.reference || '',
                                    expected_quantity: selectedProduct.stock?.toString() || '0',
                                  });
                                } else {
                                  setFormData({ ...formData, product_id: value || '', product_name: '' });
                                }
                              }}
                              options={buildInventaireProductSelectOptions(products)}
                              placeholder={p('placeholder_select_product')}
                              searchable={true}
                              required
                            />
                          </div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_expected_qty')}</label><input type="number" step="0.01" value={formData.expected_quantity} onChange={(e) => setFormData({ ...formData, expected_quantity: e.target.value })} placeholder={p('placeholder_expected_qty')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_counted_qty')} <span className="qawaem-required-mark">*</span></label><input type="number" step="0.01" required value={formData.counted_quantity} onChange={(e) => setFormData({ ...formData, counted_quantity: e.target.value })} placeholder={p('placeholder_counted_qty')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div></div>
              )}

              {inventoryFormTab === 'varianceAnalysis' && (
              <div className={INVENTAIRE_FORM_GRID}><div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_variance_quantity')}</label><input type="number" step="0.01" value={formData.variance_quantity} onChange={(e) => setFormData({ ...formData, variance_quantity: e.target.value })} placeholder={p('placeholder_variance_qty')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_variance_value_sar')}</label><input type="number" step="0.01" value={formData.variance_value} onChange={(e) => setFormData({ ...formData, variance_value: e.target.value })} placeholder={p('placeholder_variance_value')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div className="md:col-span-3"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_variance_reason')}</label><input type="text" value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder={p('placeholder_variance_reason')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div className="md:col-span-3"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="3" placeholder={p('placeholder_notes')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"></textarea></div></div>
              )}
              </form>
            </div>
        </InventaireModalPortal>
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default InventairePhysiquePage;

